import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';

import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { generateUuid } from '@recruitee/utils';
import { WorkerFunctions, WorkerTask, WorkerTaskDone } from '@recruitee/worker';

export const WorkerFactory = new InjectionToken<() => Worker>('Worker factory');

@Injectable({ providedIn: 'root' })
export class WorkerPool implements OnDestroy {
  private workers: Worker[] = [];
  private lastWorkerId = 0;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    @Inject(WorkerFactory)
    private createWorker: () => Worker,
  ) {}

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
    this.workers.forEach(worker => worker.terminate());
  }

  public init(poolSize?: number) {
    const hardwareConcurrency = navigator.hardwareConcurrency;
    const workersCount = hardwareConcurrency ? Math.min(hardwareConcurrency, 4) : 2;

    this.workers = [...Array(poolSize || workersCount)].map(() => this.createWorker());
  }

  public fillInReferences(
    ...args: Parameters<WorkerFunctions['fillInReferences']>
  ): Observable<ReturnType<WorkerFunctions['fillInReferences']>> {
    const task = this.createTask('fillInReferences', args);
    return this.exec(task);
  }

  private createTask<T extends keyof WorkerFunctions>(
    fnName: T,
    args: Parameters<WorkerFunctions[T]>,
  ): WorkerTask {
    return {
      id: generateUuid(),
      fnName,
      args,
    };
  }

  private exec(task: WorkerTask) {
    const worker = this.workers[this.lastWorkerId];
    this.lastWorkerId = (this.lastWorkerId + 1) % this.workers.length; // Change round robin to something smarter
    const result = fromEvent<MessageEvent>(worker, 'message').pipe(
      map(event => <WorkerTaskDone>event.data),
      filter(doneTask => doneTask.id === task.id),
      take(1),
      map(doneTask => doneTask.result),
      takeUntil(this.ngOnDestroy$),
    );

    worker.postMessage(task);
    return result;
  }
}
